import React, { ChangeEvent, FocusEvent, useEffect, useState } from 'react';
import { IconButton } from '@contentful/f36-button';
import { Table } from '@contentful/f36-table';
import { TextInput } from '@contentful/f36-forms';
import { DeleteIcon } from '@contentful/f36-icons';
import { PinnedProduct } from '../../types/PinnedProduct';

export interface PinnedProductsRowProps {
  pinnedProduct: PinnedProduct;
  onPinnedProductRemoved: () => void;
  onPinnedProductChanged: (pinnedProduct: PinnedProduct) => boolean;
}

const PinnedProductsRow = (props: PinnedProductsRowProps): JSX.Element => {
  const [invalid, setInvalid] = useState<boolean>(false);
  const [mpvIdValue, setMpvIdValue] = useState<string>(props.pinnedProduct.mpvId);
  const [positionValue, setPositionValue] = useState<string>(props.pinnedProduct.position.toString());

  // Keep the fields in sync when the product changes for reasons other than
  // this row's own edit -- e.g., another row's position edit shifting this
  // row's position via collision resolution.
  useEffect(() => {
    setMpvIdValue(props.pinnedProduct.mpvId);
    setPositionValue(props.pinnedProduct.position.toString());
  }, [props.pinnedProduct.mpvId, props.pinnedProduct.position]);

  const handleMpvIdInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setMpvIdValue(event.target.value);
  };

  const handlePositionInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setPositionValue(event.target.value);
  };

  const handleMpvIdChange = (event: FocusEvent<HTMLInputElement>) => {
    const newMpvId = event.target.value;
    if (newMpvId === props.pinnedProduct.mpvId) {
      return;
    }

    const succeeded: boolean = props.onPinnedProductChanged({
      mpvId: newMpvId,
      position: props.pinnedProduct.position
    });

    setInvalid(!succeeded);
  };

  const handlePositionChange = (event: FocusEvent<HTMLInputElement>) => {
    const newPosition = Number(event.target.value);
    if (newPosition === props.pinnedProduct.position) {
      return;
    }

    if (!Number.isSafeInteger(newPosition) || newPosition <= 0) {
      setPositionValue(props.pinnedProduct.position.toString());
      return;
    }

    const succeeded = props.onPinnedProductChanged({
      mpvId: props.pinnedProduct.mpvId,
      position: newPosition
    });

    if (!succeeded) {
      setPositionValue(props.pinnedProduct.position.toString());
    }
  };

  return (
    <Table.Row>
      <Table.Cell>
        <TextInput
          data-testid="mpv-id-field"
          isInvalid={invalid}
          value={mpvIdValue}
          onChange={handleMpvIdInputChange}
          onBlur={handleMpvIdChange}
        />
      </Table.Cell>
      <Table.Cell>
        <TextInput
          data-testid="position-field"
          type="number"
          value={positionValue}
          onChange={handlePositionInputChange}
          onBlur={handlePositionChange}
        />
      </Table.Cell>
      <Table.Cell>
        <IconButton
          data-testid="delete-button"
          variant="transparent"
          icon={<DeleteIcon variant="negative" size="large" />}
          aria-label="Unpin product"
          onClick={props.onPinnedProductRemoved}
        />
      </Table.Cell>
    </Table.Row>
  );
};

export default PinnedProductsRow;
